<template>
  <q-table
    v-model:selected="selected"
    :columns="columns"
    :pagination="{
      rowsPerPage: 30,
      sortBy: 'createdAt',
      descending: true,
    }"
    :rows="rows"
    binary-state-sort
    no-data-label="Zatím nejsou zaregistrovaní žádní účastníci"
    row-key="id"
    selection="multiple"
  >
    <template v-slot:top>
      <span class="text-h6">{{
        isSubstituteMode ? 'Náhradníci' : 'Registrovaní účastníci'
      }}</span>

      <q-space />

      <div class="row items-center q-gutter-sm">
        <q-btn
          v-if="selected.length > 0 && !isSubstituteMode"
          color="secondary"
          outline
          @click="$emit('generate-certificates', selected)"
        >
          <q-icon name="download" size="2.2em" />
          Osvědčení ({{ selected.length }})
        </q-btn>

        <q-btn
          :disable="selected.length === 0"
          :loading="isDeletingParticipants"
          color="red"
          round
          @click="
            $emit(
              'delete-participants',
              selected.map((participant: Participant) =>
                participant.id.toString(),
              ),
            )
          "
        >
          <q-icon name="delete" />
        </q-btn>
      </div>
    </template>

    <template v-slot:header-selection="scope">
      <q-checkbox v-model="scope.selected" />
    </template>

    <template v-slot:body="props">
      <q-tr :props="props">
        <q-td>
          <template v-if="isSubstituteMode">
            <q-checkbox v-model="props.selected" color="primary" />
            <q-btn
              :disable="!isPossibleToMoveSubstituteToParticipants"
              color="primary"
              size="xs"
              rounded
              outline
              dense
              @click="updateRow(props.row, 'isSubstitute', false)"
            >
              <q-icon name="arrow_circle_up" />
              <q-tooltip>
                <template v-if="isPossibleToMoveSubstituteToParticipants">
                  Přesunout do účastníků.
                </template>
                <div v-else>
                  <div>Nelze přesunout do účastníků.</div>
                  <div>Kapacita je naplňena.</div>
                  <div>Nejdříve smažte některého účastníka</div>
                </div>
              </q-tooltip>
            </q-btn>
          </template>
          <template v-else>
            <q-checkbox v-model="props.selected" color="primary" />
            <q-btn
              v-if="
                !isSubstituteMode &&
                seminar.capacity &&
                seminar.participants &&
                seminar.capacity <= seminar.participants?.length
              "
              color="primary"
              class="q-ml-sm"
              size="xs"
              rounded
              outline
              dense
              @click="updateRow(props.row, 'isSubstitute', true)"
            >
              <q-icon name="arrow_circle_down" />
              <q-tooltip
                >Přesunout do náhradníků.
                <template
                  >Kapacita je naplňena. Nejdříve smažte některého
                  účastníka</template
                >
              </q-tooltip>
            </q-btn>
          </template>
        </q-td>

        <q-td key="name" :props="props">
          {{ props.row.name }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.name"
            buttons
            @save="updateRow(props.row, 'name', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="dateOfBirth" :props="props">
          {{ convertToQuasarDate(new Date(props.row.dateOfBirth), false) }}
          <q-popup-edit
            v-slot="scope"
            :model-value="
              convertToQuasarDate(new Date(props.row.dateOfBirth), false)
            "
            buttons
            @save="
              updateRow(props.row, 'dateOfBirth', convertFromQuasarDate($event))
            "
          >
            <q-input
              v-model="scope.value"
              :mask="dateOnly_InputMask"
              autofocus
              counter
              dense
              hint="Ve formátu 01. 01. 2023"
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="paymentMethod" :props="props">
          {{ translatePaymentMethodToCzech(props.row.paymentMethod) }}
          <q-popup-edit
            v-slot="scope"
            :model-value="
              translatePaymentMethodToCzech(props.row.paymentMethod)
            "
            buttons
            @save="
              updateRow(
                props.row,
                'paymentMethod',
                translatePaymentMethodToEnum($event),
              )
            "
          >
            <q-select
              v-model="scope.value"
              :options="translatedPaymentMethods"
              autofocus
              label="Platba"
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="email" :props="props">
          {{ props.row.email }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.email"
            buttons
            @save="updateRow(props.row, 'email', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="phone" :props="props">
          {{ props.row.phone }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.phone"
            buttons
            @save="updateRow(props.row, 'phone', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="school" :props="props">
          {{ props.row.school }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.school"
            buttons
            @save="updateRow(props.row, 'school', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="ico" :props="props">
          {{ props.row.ico }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.ico"
            buttons
            @save="updateRow(props.row, 'ico', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="invoiceEmail" :props="props">
          {{ props.row.invoiceEmail }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.invoiceEmail"
            buttons
            @save="updateRow(props.row, 'invoiceEmail', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="note" :props="props">
          {{ props.row.note }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.note"
            buttons
            @save="updateRow(props.row, 'note', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="createdAt" :props="props">
          {{ convertToQuasarDate(new Date(props.row.createdAt)) }}
        </q-td>

        <q-td key="wantsAllowance" :props="props">
          {{ props.row.placeOfBirth }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.placeOfBirth"
            buttons
            @save="updateRow(props.row, 'placeOfBirth', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>
        
        <q-td key="wantsAllowance" :props="props">
          <div class="row items-center justify-center">
            <q-icon
              class="block"
              name="check_icon"
              v-if="props.row.wantsAllowance"
            />
            <q-icon class="block" name="close_icon" v-else />
          </div>
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.wantsAllowance"
            buttons
            @save="updateRow(props.row, 'wantsAllowance', Boolean($event))"
          >
            <q-checkbox v-model="scope.value" />
          </q-popup-edit>
        </q-td>

        <q-td key="branchOffice" :props="props">
          {{ props.row.branchOffice }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.branchOffice"
            buttons
            @save="updateRow(props.row, 'branchOffice', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="education" :props="props">
          {{ props.row.education }}
          <q-popup-edit
            v-slot="scope"
            v-model="props.row.education"
            buttons
            @save="updateRow(props.row, 'education', $event)"
          >
            <q-input
              v-model="scope.value"
              autofocus
              counter
              dense
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>

        <q-td key="fileUrls" :props="props">
          <div
            v-if="props.row.fileUrls && props.row.fileUrls.length > 0"
            class="row inline items-center q-gutter-x-sm no-wrap justify-center"
          >
            <div
              class="row items-center q-gutter-x-sm no-wrap justify-center"
              style="max-width: 100px"
            >
              <div
                v-for="(fileUrl, index) in props.row.fileUrls"
                :key="index"
                class="relative-position file-container"
                style="position: relative"
              >
                <a :href="fileUrl" target="_blank">
                  <q-icon
                    :name="getFileIcon(fileUrl)"
                    size="25px"
                    color="primary"
                  >
                    <q-tooltip>
                      {{ getFileName(fileUrl) }}
                    </q-tooltip>
                  </q-icon>
                </a>
                <q-btn
                  @click="deleteFile(props.row, index)"
                  round
                  dense
                  size="xs"
                  color="red"
                  icon="close"
                  class="delete-btn absolute-top-right"
                />
              </div>
            </div>
          </div>
          <q-file
            v-model="qFileModelValue"
            :ref="`file-${props.row.id}`"
            multiple
            accept=".pdf, image/*"
            style="display: none"
            @update:model-value="(files) => handleFileUpload(files, props.row)"
          />
          <q-btn
            @click="($refs[`file-${props.row.id}`] as any).pickFiles()"
            outline
            size="sm"
            color="primary"
            class="q-ml-md"
            label="Přidat"
            :loading="isUploadingFiles"
          />
        </q-td>
      </q-tr>
    </template>
  </q-table>
</template>

<script lang="ts" setup>
import type { Participant } from '@/components/seminar/Participant/types';
import { computed, ref, toRaw, toRefs, watch } from 'vue';
import {
  convertFromQuasarDate,
  convertToQuasarDate,
  dateOnly_InputMask,
} from '@/components/seminar/translators/date';
import {
  translatedPaymentMethods,
  translatePaymentMethodToCzech,
  translatePaymentMethodToEnum,
} from 'payment-method-translator';
import { useFilesUpload } from '@/utils/files/useFilesUpload';
import type {
  GetSeminarsItem,
  ParticularSeminar,
} from '@/components/seminar/SeminarTable/types';
import { SeminarType } from '@/api/graphql/codegen/graphql';
import { type QTableColumn, useQuasar } from 'quasar';

const qFileModelValue = ref(null); // this is not needed as we do not display the files in the button
const $q = useQuasar();

const {
  participants,
  isDeletingParticipants,
  seminar,
  isSubstituteMode = false,
} = defineProps<{
  participants: Participant[];
  isDeletingParticipants: boolean;
  seminar: ParticularSeminar;
  isSubstituteMode?: boolean;
}>();

const hasSeminarDefinedCapacityAndSubstituteCapacity = computed(() => {
  return seminar.capacity && seminar.substituteCapacity;
});

const isPossibleToMoveSubstituteToParticipants = computed(() => {
  return Boolean(
    seminar.capacity &&
      seminar.capacity >
        (seminar.participants?.filter((p) => !p.isSubstitute).length ?? 0),
  );
});

const emit = defineEmits<{
  (e: 'update:participant', participant: Participant): void;
  (e: 'delete-participants', selectedParticipantIds: string[]): void;
  (e: 'generate-certificates', selectedParticipants: Participant[]): void;
}>();

const columns = computed(() => {
  const baseColumns: QTableColumn[] = [
    {
      name: 'name',
      required: true,
      label: 'Jméno',
      align: 'left' as const,
      field: (row: Participant) => row.name,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'dateOfBirth',
      required: true,
      label: 'Datum narození',
      align: 'left' as const,
      field: (row: Participant) => row.dateOfBirth,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'paymentMethod',
      required: true,
      label: 'Typ platby',
      align: 'left' as const,
      field: (row: Participant) => row.paymentMethod,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'email',
      required: true,
      label: 'Email účastníka',
      align: 'left' as const,
      field: (row: Participant) => row.email,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'phone',
      required: true,
      label: 'Telefon',
      align: 'left' as const,
      field: (row: Participant) => row.phone,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'school',
      required: true,
      label: 'Název školy',
      align: 'left' as const,
      style: 'width: 500px',
      field: (row: Participant) => row.school,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'ico',
      required: true,
      label: 'IČO',
      align: 'left' as const,
      style: 'width: 500px',
      field: (row: Participant) => row.ico,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'invoiceEmail',
      required: true,
      label: 'Fakturační email',
      align: 'left' as const,
      style: 'width: 500px',
      field: (row: Participant) => row.invoiceEmail,
      format: (val: string) => `${val}`,
      sortable: true,
    },
    {
      name: 'note',
      required: true,
      label: 'Poznámka',
      align: 'left' as const,
      style: 'width: 100px; max-width: 300px; overflow: hidden',
      field: (row: Participant) => row.note,
      format: (val: string) => `${val}`,
      sortable: false,
    },
    {
      name: 'createdAt',
      required: true,
      label: 'Datum registrace',
      align: 'left' as const,
      field: (row: Participant) => row.createdAt,
      format: (val: string) => `${val}`,
      sortable: true,
    },
  ];

  // Only add fileUrls column if seminar type is STUDIUM
  if (seminar.type === SeminarType.Studium) {
    baseColumns.push({
      name: 'placeOfBirth',
      required: true,
      label: 'Místo narození',
      align: 'left' as const,
      field: (row: Participant) => row.placeOfBirth,
      format: (val: string) => `${val}`,
      sortable: true,
    });
    
    baseColumns.push({
      name: 'wantsAllowance',
      required: true,
      label: 'Žádá o příspěvek',
      align: 'left' as const,
      field: (row: Participant) => row.wantsAllowance,
      format: (val: boolean) => `${val}`,
      sortable: true,
    });

    baseColumns.push({
      name: 'branchOffice',
      required: false,
      label: 'Pobočka',
      align: 'left' as const,
      field: (row: Participant) => row.branchOffice,
      format: (val: string) => `${val}`,
    });

    baseColumns.push({
      name: 'education',
      required: true,
      label: 'Dosažené vzdělání',
      align: 'left' as const,
      field: (row: Participant) => row.education,
      format: (val) => `${val}`,
      sortable: true,
    });
    baseColumns.push({
      name: 'fileUrls',
      required: true,
      label: 'Dokumenty',
      align: 'left' as const,
      field: (row: Participant) => row.fileUrls,
      format: (val) => `${val}`,
      sortable: true,
    });
  }

  return baseColumns;
});

const { uploadFiles, isPending: isUploadingFiles } = useFilesUpload();

const rows = ref<Participant[]>([]);
watch(
  () => participants,
  (participants) => {
    // Remove potentially problematic properties before cloning
    const cleanParticipants = toRaw(participants).map((p) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, ...rest } = p;
      return rest;
    });
    rows.value = structuredClone(cleanParticipants) as Participant[];
  },
  { immediate: true },
);

const updateRow = async (
  participant: Participant,
  columnName: keyof Participant,
  newValue: unknown,
) => {
  if (!Object.keys(participant).includes(columnName)) {
    throw new Error(`Participant doesn't contain column name: ${columnName}`);
  }
  Object.assign(participant, { [columnName]: newValue });
  emit('update:participant', participant);
};

const selected = ref<Participant[]>([]);

const handleFileUpload = async (files: File[], participant: Participant) => {
  try {
    const publicUrls = await uploadFiles(files);

    // Update participant's fileUrls with the new public URLs
    const existingUrls = participant.fileUrls || [];
    const updatedUrls = [...existingUrls, ...publicUrls];

    await updateRow(participant, 'fileUrls', updatedUrls);
  } catch (error) {
    console.error('Error uploading files:', error);
    // You might want to show a notification to the user here
  }
};

const getFileName = (url: string): string => {
  return url.split('/').pop() || '';
};

const getFileIcon = (url: string): string => {
  const fileName = getFileName(url).toLowerCase();
  const extension = fileName.split('.').pop();

  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image';
    case 'png':
      return 'image';
    case 'gif':
      return 'image';
    case 'webp':
      return 'image';
    case 'svg':
      return 'image';
    case 'pdf':
      return 'picture_as_pdf';
    case 'doc':
    case 'docx':
      return 'description';
    case 'txt':
      return 'text_snippet';
    case 'zip':
    case 'rar':
    case '7z':
      return 'archive';
    default:
      return 'attach_file';
  }
};

const deleteFile = async (participant: Participant, fileIndex: number) => {
  if (
    !participant.fileUrls ||
    fileIndex < 0 ||
    fileIndex >= participant.fileUrls.length
  ) {
    return;
  }

  const fileName = getFileName(participant.fileUrls[fileIndex]);

  // Show confirmation dialog
  $q.dialog({
    title: 'Potvrdit smazání',
    message: `Opravdu chcete smazat soubor "${fileName}"?`,
    persistent: true,
    ok: {
      label: 'Smazat',
      color: 'red',
    },
    cancel: {
      label: 'Zrušit',
      color: 'grey',
    },
  }).onOk(async () => {
    // Create a new array without the file at the specified index
    const updatedFileUrls = participant.fileUrls!.filter(
      (_, index) => index !== fileIndex,
    );

    // Update the participant with the new fileUrls array
    await updateRow(participant, 'fileUrls', updatedFileUrls);
  });
};
</script>

<style scoped>
.delete-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 1;
  transition: opacity 0.2s ease;
  opacity: 0;
}

.file-container:hover .delete-btn {
  opacity: 1;
}
</style>
